import React from 'react';
import DashboardCard from './DashboardCard';
import type { CostBreakdownItem } from './types';

interface LocationDonutChartProps {
  items: CostBreakdownItem[];
  total: number;
  loading?: boolean;
}

const SIZE = 120;
const STROKE = 24;
const RADIUS = (SIZE - STROKE) / 2;
const CIRCUMFERENCE = 2 * Math.PI * RADIUS;

const LocationDonutChart: React.FC<LocationDonutChartProps> = ({ items, total, loading }) => {
  if (loading) {
    return (
      <DashboardCard className="h-full" title="Labour Cost by Location">
        <div className="px-5 pb-5 flex-1 animate-pulse bg-gray-50 rounded-lg mx-5 mb-5 min-h-[220px]" />
      </DashboardCard>
    );
  }

  let offset = 0;
  const displayTotal = total > 0 ? total : items.reduce((s, i) => s + i.value, 0);

  return (
    <DashboardCard className="h-full w-full flex flex-col" title="Labour Cost by Location">
      <div className="px-5 pb-5 flex-1 flex flex-col justify-center min-h-[220px]">
        {items.length === 0 ? (
          <p className="text-sm text-gray-500 w-full text-center py-6">No location breakdown available.</p>
        ) : (
          <div className="flex flex-col gap-4 flex-1 justify-center">
            <div className="relative flex-shrink-0 mx-auto" style={{ width: SIZE, height: SIZE }}>
              <svg width={SIZE} height={SIZE} className="-rotate-90">
                <circle
                  cx={SIZE / 2}
                  cy={SIZE / 2}
                  r={RADIUS}
                  fill="none"
                  stroke="#F3F4F6"
                  strokeWidth={STROKE}
                />
                {items.map((item) => {
                  const segment = displayTotal > 0 ? (item.value / displayTotal) * CIRCUMFERENCE : 0;
                  const dashArray = `${segment} ${CIRCUMFERENCE - segment}`;
                  const dashOffset = -offset;
                  offset += segment;
                  return (
                    <circle
                      key={item.name}
                      cx={SIZE / 2}
                      cy={SIZE / 2}
                      r={RADIUS}
                      fill="none"
                      stroke={item.color}
                      strokeWidth={STROKE}
                      strokeDasharray={dashArray}
                      strokeDashoffset={dashOffset}
                    />
                  );
                })}
              </svg>
              <div className="absolute inset-0 flex flex-col items-center justify-center">
                <span className="text-sm font-bold text-gray-900">
                  €{Math.round(displayTotal).toLocaleString('en-IE')}
                </span>
                <span className="text-[10px] text-gray-500">Total</span>
              </div>
            </div>

            <div className="space-y-2 min-w-0">
              {items.map((item) => (
                <div key={item.name} className="flex items-center justify-between text-xs gap-2">
                  <div className="flex items-center gap-2 min-w-0">
                    <span
                      className="w-2.5 h-2.5 rounded-full flex-shrink-0"
                      style={{ backgroundColor: item.color }}
                    />
                    <span className="text-gray-700 truncate">{item.name}</span>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <span className="font-medium text-gray-900">€{item.value.toLocaleString('en-IE')}</span>
                    <span className="text-gray-400 w-10 text-right">{item.percent}%</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </DashboardCard>
  );
};

export default LocationDonutChart;
